if (!window.__spotixCondensedHeaderBound) {
  window.__spotixCondensedHeaderBound = true;

  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  // The Searchspring bundle binds its own click handler to the trigger and calls
  // stopPropagation(), and closes the modal by removing the `open` class directly.
  // So everything here listens in the capture phase or watches the class instead
  // of relying on bubbling clicks.
  let lastTrigger = null;

  const getSearch = () => document.querySelector('#header-component .header-search');

  const getTriggers = () => document.querySelectorAll('[data-header-search-trigger]');

  const getFocusable = (search) =>
    Array.from(search.querySelectorAll(FOCUSABLE)).filter(
      (el) => el.getClientRects().length > 0 && !el.closest('[aria-hidden="true"]') && el.getAttribute('aria-hidden') !== 'true'
    );

  const enhanceCloseButton = (search) => {
    const close = search.querySelector('.close');
    if (!close || close.getAttribute('role') === 'button') return;

    close.setAttribute('role', 'button');
    close.setAttribute('tabindex', '0');
    close.setAttribute('aria-label', 'Close search');
  };

  // The bundle renders an icon-only submit button and an input with aria-label="".
  const enhanceSearchControls = (search) => {
    const submit = search.querySelector('.header-search-submit');
    if (submit && !submit.getAttribute('aria-label')) {
      submit.setAttribute('aria-label', 'Submit search');
      submit.querySelector('svg')?.setAttribute('aria-hidden', 'true');
    }

    const input = search.querySelector('.query-box_container input');
    if (input && !input.getAttribute('aria-label')) input.setAttribute('aria-label', 'Search');
  };

  // The Products / Articles tabs are rendered by the Searchspring bundle as plain divs,
  // so expose them as an ARIA tablist with a roving tabindex.
  const TAB_PANELS = ['.search-store', '.blog-store'];

  const enhanceTabs = (search) => {
    const tablist = search.querySelector('.search-tabs-selector');
    if (!tablist) return;

    tablist.setAttribute('role', 'tablist');
    tablist.setAttribute('aria-label', 'Search results');

    Array.from(tablist.children).forEach((tab, index) => {
      const selected = tab.classList.contains('active');
      const tabId = `header-search-tab-${index}`;
      const panel = search.querySelector(TAB_PANELS[index]);

      tab.id = tabId;
      tab.setAttribute('role', 'tab');
      tab.setAttribute('aria-selected', String(selected));
      tab.setAttribute('tabindex', selected ? '0' : '-1');

      if (panel) {
        panel.id = `header-search-panel-${index}`;
        panel.setAttribute('role', 'tabpanel');
        panel.setAttribute('aria-labelledby', tabId);
        tab.setAttribute('aria-controls', panel.id);
      }
    });
  };

  const handleTabKeydown = (event) => {
    const tab = event.target.closest?.('.header-search .search-tabs-selector [role="tab"]');
    if (!tab) return false;

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      tab.click();
      return true;
    }

    const tabs = Array.from(tab.parentElement.querySelectorAll('[role="tab"]'));
    const index = tabs.indexOf(tab);
    const targets = {
      ArrowRight: tabs[(index + 1) % tabs.length],
      ArrowLeft: tabs[(index - 1 + tabs.length) % tabs.length],
      Home: tabs[0],
      End: tabs[tabs.length - 1],
    };
    const next = targets[event.key];
    if (!next) return false;

    event.preventDefault();
    next.focus();
    next.click();
    return true;
  };

  const closeSearch = (search) => {
    search.classList.remove('open');
    document.body.style.height = '';
  };

  const onOpened = (search) => {
    search.setAttribute('role', 'dialog');
    search.setAttribute('aria-modal', 'true');
    search.setAttribute('aria-label', 'Search');
    enhanceCloseButton(search);
    getTriggers().forEach((button) => button.setAttribute('aria-expanded', 'true'));

    requestAnimationFrame(() => {
      if (!search.contains(document.activeElement)) {
        search.querySelector('.query-box_container input')?.focus();
      }
    });
  };

  const onClosed = (search) => {
    search.removeAttribute('aria-modal');
    getTriggers().forEach((button) => button.setAttribute('aria-expanded', 'false'));

    const active = document.activeElement;
    const focusWasInSearch = !active || active === document.body || search.contains(active);
    const trigger = lastTrigger && lastTrigger.isConnected ? lastTrigger : getTriggers()[0];

    if (focusWasInSearch) trigger?.focus();
  };

  const watchSearch = (search) => {
    search.dataset.a11yWatched = 'true';
    let wasOpen = search.classList.contains('open');

    enhanceSearchControls(search);

    new MutationObserver(() => {
      const isOpen = search.classList.contains('open');
      enhanceSearchControls(search);
      if (isOpen) {
        enhanceCloseButton(search);
        enhanceTabs(search);
      }
      if (isOpen === wasOpen) return;

      wasOpen = isOpen;
      isOpen ? onOpened(search) : onClosed(search);
    }).observe(search, { attributes: true, attributeFilter: ['class'], childList: true, subtree: true });
  };

  const initialSearch = getSearch();
  if (initialSearch) watchSearch(initialSearch);

  // The theme editor re-renders the header section, replacing the search element.
  document.addEventListener('shopify:section:load', () => {
    const search = getSearch();
    if (search && !search.dataset.a11yWatched) watchSearch(search);
  });

  document.addEventListener(
    'click',
    (event) => {
      const trigger = event.target.closest('[data-header-search-trigger]');
      if (!trigger) return;

      event.preventDefault();

      const header = trigger.closest('header-component') || document.querySelector('header-component');
      const search = header?.querySelector('.header-search');
      if (!search) return;

      lastTrigger = trigger;
      search.classList.add('open');
      document.body.style.height = `${window.innerHeight}px`;
    },
    true
  );

  document.addEventListener('click', (event) => {
    const search = event.target.closest('.header-search');
    if (!search || event.target !== search) return;

    closeSearch(search);
  });

  document.addEventListener(
    'keydown',
    (event) => {
      const search = document.querySelector('.header-search.open');
      if (!search) return;

      const keyboardClose = event.target.closest?.('.header-search .close');
      if (keyboardClose && (event.key === 'Enter' || event.key === ' ')) {
        event.preventDefault();
        keyboardClose.click();
        return;
      }

      if (handleTabKeydown(event)) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        closeSearch(search);
        return;
      }

      if (event.key !== 'Tab') return;

      const focusable = getFocusable(search);
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;

      if (!search.contains(active)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      } else if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    },
    true
  );

  // Safety net for focus arriving outside the modal by other means (e.g. screen reader virtual cursor).
  document.addEventListener('focusin', (event) => {
    const search = document.querySelector('.header-search.open');
    if (!search || search.contains(event.target)) return;

    (getFocusable(search)[0] || search.querySelector('.query-box_container input'))?.focus();
  });
}
